import { useQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";

import { can } from "@devengo/shared";

import {
  Badge,
  Card,
  ErrorBox,
  PageTitle,
  Select,
  tableClass,
  tdClass,
  thClass,
} from "../components/ui";
import { api } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useCompanies } from "../lib/hooks";
import { useCurrentUser } from "../lib/session";

interface AuditEvent {
  id: string;
  occurredAt: string;
  actorLabel: string;
  action: string;
  entityType: string;
  entityId: string | null;
  companyId: string | null;
  reason: string | null;
  severity: "info" | "important" | "critical";
  channel: "web" | "mcp" | "cli" | "system";
  clientName: string | null;
  beforeState: unknown;
  afterState: unknown;
  requestId: string | null;
}

const ACTIONS: Record<string, string> = {
  "tenant.created": "Alta del estudio",
  "tenant.branding_updated": "Cambio de marca",
  "user.created": "Alta de usuario",
  "user.updated": "Cambio de usuario",
  "user.role_changed": "Cambio de rol",
  "user.password_reset": "Cambio de clave",
  "session.login_succeeded": "Ingreso",
  "session.login_failed": "Ingreso fallido",
  "session.login_throttled": "Ingreso bloqueado por intentos",
  "session.logged_out": "Salida",
  "company.created": "Alta de empresa",
  "counterpart.created": "Alta de contraparte",
  "counterpart.updated": "Cambio de contraparte",
  "voucher.created": "Alta de comprobante",
  "voucher.updated": "Edición de comprobante",
  "voucher.deleted": "Baja de comprobante",
  "voucher.voided": "Anulación de comprobante",
  "voucher.unvoided": "Reactivación de comprobante",
  "settlement.voided": "Anulación de liquidación",
  "settlement.unvoided": "Reactivación de liquidación",
  "voucher.moved": "Cambio de período",
  "period.created": "Alta de período",
  "period.status_changed": "Cambio de estado del período",
  "period.reopened": "Reapertura de período",
  "period.balances_updated": "Saldos F.2051",
  "vat_position.calculated": "Caja IVA guardada",
  "file.uploaded": "Archivo subido",
  "import.staged": "Importación analizada",
  "import.committed": "Importación confirmada",
  "import.discarded": "Importación descartada",
  "audit.exported": "Exportación de auditoría",
  "oauth.client_registered": "Asistente registrado",
  "oauth.authorization_granted": "Asistente autorizado",
  "oauth.authorization_denied": "Asistente rechazado",
  "oauth.grant_created": "Asistente conectado",
  "oauth.grant_revoked": "Asistente desconectado",
  "oauth.grant_revoked_by_client": "Asistente desconectado (por el cliente)",
  "oauth.refresh_replay_detected": "Asistente cortado: token reutilizado",
  "import.failed": "Importación fallida",
};

const TONES = { info: "neutral", important: "brand", critical: "danger" } as const;

export function AuditPage() {
  const user = useCurrentUser();
  const companies = useCompanies();
  const [severity, setSeverity] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [channel, setChannel] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const params = new URLSearchParams({
    ...(severity ? { severity } : {}),
    ...(companyId ? { companyId } : {}),
    ...(channel ? { channel } : {}),
  });
  const events = useQuery({
    queryKey: ["audit", severity, companyId, channel],
    queryFn: () => api.get<AuditEvent[]>(`/api/audit?${params.toString()}`),
    enabled: can(user.role, "viewAudit"),
  });
  const companyName = (id: string | null) =>
    companies.data?.find((c) => c.id === id)?.legalName ?? "";

  if (!can(user.role, "viewAudit")) {
    return (
      <>
        <PageTitle>Auditoría</PageTitle>
        <Card>
          <p className="text-sm text-brand-muted">
            La auditoría la consulta solo el Dueño del estudio.
          </p>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageTitle
        subtitle="Todo lo que cambió en el estudio, quién lo hizo y por qué. Los registros no se pueden editar ni borrar."
        actions={
          <>
            <Select
              className="w-44"
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              aria-label="Severidad"
            >
              <option value="">Todas las severidades</option>
              <option value="critical">Solo críticas</option>
              <option value="important">Importantes</option>
              <option value="info">Informativas</option>
            </Select>
            <Select
              className="w-44"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              aria-label="Canal"
            >
              <option value="">Todos los canales</option>
              <option value="web">Pantallas</option>
              <option value="mcp">Asistente (IA)</option>
              <option value="cli">Plataforma</option>
            </Select>
            <Select
              className="w-56"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              aria-label="Empresa"
            >
              <option value="">Todas las empresas</option>
              {companies.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.legalName}
                </option>
              ))}
            </Select>
            <a
              href={`/api/audit/export.xlsx?${params.toString()}`}
              className="rounded-md border border-brand-line bg-white px-3 py-1.5 text-sm hover:bg-brand-tint"
            >
              Exportar Excel
            </a>
          </>
        }
      >
        Auditoría
      </PageTitle>
      <ErrorBox error={events.error} />
      <Card>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={thClass}>Fecha</th>
              <th className={thClass}>Quién</th>
              <th className={thClass}>Qué</th>
              <th className={thClass}>Empresa</th>
              <th className={thClass}>Motivo</th>
              <th className={thClass} />
            </tr>
          </thead>
          <tbody>
            {events.data?.map((e) => (
              <Fragment key={e.id}>
                <tr className={e.severity === "critical" ? "bg-red-50" : ""}>
                  <td className={`${tdClass} whitespace-nowrap tabular`}>
                    {formatDateTime(e.occurredAt)}
                  </td>
                  <td className={tdClass}>
                    {e.actorLabel}
                    {e.channel === "mcp" ? (
                      <div className="text-xs text-brand-primary">
                        vía asistente{e.clientName ? ` · ${e.clientName}` : ""}
                      </div>
                    ) : null}
                  </td>
                  <td className={tdClass}>
                    <Badge tone={TONES[e.severity]}>{ACTIONS[e.action] ?? e.action}</Badge>
                  </td>
                  <td className={tdClass}>{companyName(e.companyId)}</td>
                  <td className={tdClass}>{e.reason ?? ""}</td>
                  <td className={`${tdClass} text-right`}>
                    <button
                      type="button"
                      className="text-xs text-brand-primary hover:underline"
                      onClick={() => setOpen(open === e.id ? null : e.id)}
                    >
                      {open === e.id ? "Ocultar" : "Detalle"}
                    </button>
                  </td>
                </tr>
                {open === e.id ? (
                  <tr>
                    <td className={tdClass} colSpan={6}>
                      <div className="grid grid-cols-1 gap-2 text-xs md:grid-cols-2">
                        <pre className="overflow-auto rounded bg-brand-tint p-2">
                          Antes: {JSON.stringify(e.beforeState, null, 2)}
                        </pre>
                        <pre className="overflow-auto rounded bg-brand-tint p-2">
                          Después: {JSON.stringify(e.afterState, null, 2)}
                        </pre>
                      </div>
                      <p className="mt-1 text-xs text-brand-muted">
                        Código de request: {e.requestId}
                      </p>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}
